/*
  Pixelpa — Photoshop-style modal color picker.

  Self-contained UI component. Construct once; call `open(initial)` to show.
  Resolves with the picked color on OK / Enter, or `null` on Cancel / Escape /
  outside-click.

  Color math (rgbToHsv, hsvToRgb, hexToRgb, rgbToHex) is exported for tests.
  Internally the picker keeps the source-of-truth as HSV (h: 0-360, s/v: 0-1)
  to preserve hue when the user drags into a pure black/white corner.
*/

export interface RGB { r: number; g: number; b: number; }
export interface HSV { h: number; s: number; v: number; }
export interface CMYK { c: number; m: number; y: number; k: number; }
export interface Lab { l: number; a: number; b: number; }

// ---------- Color math (pure, exported for tests) ----------

export function clamp(n: number, lo: number, hi: number): number {
  return n < lo ? lo : n > hi ? hi : n;
}

export function rgbToHsv(r: number, g: number, b: number): HSV {
  const rn = clamp(r, 0, 255) / 255;
  const gn = clamp(g, 0, 255) / 255;
  const bn = clamp(b, 0, 255) / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === rn) {
      h = ((gn - bn) / d) % 6;
    } else if (max === gn) {
      h = (bn - rn) / d + 2;
    } else {
      h = (rn - gn) / d + 4;
    }
    h *= 60;
    if (h < 0) h += 360;
  }
  const s = max === 0 ? 0 : d / max;
  const v = max;
  return { h, s, v };
}

export function hsvToRgb(h: number, s: number, v: number): RGB {
  // h normalized to [0, 360)
  let hh = h % 360;
  if (hh < 0) hh += 360;
  const ss = clamp(s, 0, 1);
  const vv = clamp(v, 0, 1);
  const c = vv * ss;
  const hPrime = hh / 60;
  const x = c * (1 - Math.abs((hPrime % 2) - 1));
  let r1 = 0, g1 = 0, b1 = 0;
  if (hPrime >= 0 && hPrime < 1) { r1 = c; g1 = x; b1 = 0; }
  else if (hPrime < 2)           { r1 = x; g1 = c; b1 = 0; }
  else if (hPrime < 3)           { r1 = 0; g1 = c; b1 = x; }
  else if (hPrime < 4)           { r1 = 0; g1 = x; b1 = c; }
  else if (hPrime < 5)           { r1 = x; g1 = 0; b1 = c; }
  else                           { r1 = c; g1 = 0; b1 = x; }
  const m = vv - c;
  return {
    r: Math.round((r1 + m) * 255),
    g: Math.round((g1 + m) * 255),
    b: Math.round((b1 + m) * 255),
  };
}

export function rgbToHex(r: number, g: number, b: number): string {
  const to = (n: number) => clamp(Math.round(n), 0, 255).toString(16).padStart(2, "0");
  return `${to(r)}${to(g)}${to(b)}`.toUpperCase();
}

export function hexToRgb(input: string): RGB | null {
  let s = input.trim().toLowerCase();
  if (s.startsWith("#")) s = s.slice(1);
  if (s.length === 3) {
    if (!/^[0-9a-f]{3}$/.test(s)) return null;
    const r = parseInt(s[0]! + s[0]!, 16);
    const g = parseInt(s[1]! + s[1]!, 16);
    const b = parseInt(s[2]! + s[2]!, 16);
    return { r, g, b };
  }
  if (s.length === 6) {
    if (!/^[0-9a-f]{6}$/.test(s)) return null;
    return {
      r: parseInt(s.slice(0, 2), 16),
      g: parseInt(s.slice(2, 4), 16),
      b: parseInt(s.slice(4, 6), 16),
    };
  }
  return null;
}

// Snap RGB to the 216-color web-safe palette (0, 51, 102, 153, 204, 255).
export function snapWebSafe(rgb: RGB): RGB {
  const snap = (n: number) => Math.round(clamp(n, 0, 255) / 51) * 51;
  return { r: snap(rgb.r), g: snap(rgb.g), b: snap(rgb.b) };
}

// ---------- CMYK conversion ----------

export function rgbToCmyk(r: number, g: number, b: number): CMYK {
  const rn = clamp(r, 0, 255) / 255;
  const gn = clamp(g, 0, 255) / 255;
  const bn = clamp(b, 0, 255) / 255;
  const k = 1 - Math.max(rn, gn, bn);
  if (k === 1) return { c: 0, m: 0, y: 0, k: 1 };
  return {
    c: (1 - rn - k) / (1 - k),
    m: (1 - gn - k) / (1 - k),
    y: (1 - bn - k) / (1 - k),
    k,
  };
}

export function cmykToRgb(c: number, m: number, y: number, k: number): RGB {
  const cc = clamp(c, 0, 1);
  const cm = clamp(m, 0, 1);
  const cy = clamp(y, 0, 1);
  const ck = clamp(k, 0, 1);
  return {
    r: Math.round(255 * (1 - cc) * (1 - ck)),
    g: Math.round(255 * (1 - cm) * (1 - ck)),
    b: Math.round(255 * (1 - cy) * (1 - ck)),
  };
}

// ---------- Lab (CIE L*a*b*) conversion ----------

// sRGB linearization
function linearize(c: number): number {
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

function delinearize(c: number): number {
  return c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
}

// D50 reference white — the Lab tab here targets Photoshop's Lab working
// space (D50 / the ICC profile connection space convention), matching the
// separate Image > Mode > Lab Color pipeline in modeConversions.ts. sRGB's
// primaries are defined relative to D65, so a straight white-point swap
// without chromatic adaptation would shift hues; the XYZ computed via the
// D65-relative sRGB matrix below is Bradford-adapted to/from D50 around the
// Lab step (#i221).
const XN = 0.96422;
const YN = 1.00000;
const ZN = 0.82521;

// Bradford chromatic-adaptation matrices (Lindbloom), applied to
// normalized (0-1 scale) XYZ.
const BRADFORD_D65_TO_D50: readonly (readonly number[])[] = [
  [1.0478112, 0.0228866, -0.0501270],
  [0.0295424, 0.9904844, -0.0170491],
  [-0.0092345, 0.0150436, 0.7521316],
];
const BRADFORD_D50_TO_D65: readonly (readonly number[])[] = [
  [0.9555766, -0.0230393, 0.0631636],
  [-0.0282895, 1.0099416, 0.0210077],
  [0.0122982, -0.0204830, 1.3299098],
];

function applyMatrix3(
  m: readonly (readonly number[])[],
  x: number,
  y: number,
  z: number,
): [number, number, number] {
  return [
    m[0]![0]! * x + m[0]![1]! * y + m[0]![2]! * z,
    m[1]![0]! * x + m[1]![1]! * y + m[1]![2]! * z,
    m[2]![0]! * x + m[2]![1]! * y + m[2]![2]! * z,
  ];
}

function labF(t: number): number {
  return t > 0.008856 ? Math.cbrt(t) : (903.3 * t + 16) / 116;
}

function labFInv(t: number): number {
  const t3 = t * t * t;
  return t3 > 0.008856 ? t3 : (116 * t - 16) / 903.3;
}

export function rgbToLab(r: number, g: number, b: number): Lab {
  const rl = linearize(clamp(r, 0, 255) / 255);
  const gl = linearize(clamp(g, 0, 255) / 255);
  const bl = linearize(clamp(b, 0, 255) / 255);

  const x65 = 0.4124564 * rl + 0.3575761 * gl + 0.1804375 * bl;
  const y65 = 0.2126729 * rl + 0.7151522 * gl + 0.0721750 * bl;
  const z65 = 0.0193339 * rl + 0.1191920 * gl + 0.9503041 * bl;

  // Bradford-adapt D65 → D50 before computing Lab against the D50 white point.
  const [x, y, z] = applyMatrix3(BRADFORD_D65_TO_D50, x65, y65, z65);

  const fx = labF(x / XN);
  const fy = labF(y / YN);
  const fz = labF(z / ZN);

  return {
    l: clamp(116 * fy - 16, 0, 100),
    a: 500 * (fx - fy),
    b: 200 * (fy - fz),
  };
}

export function labToRgb(l: number, a: number, b: number): RGB {
  const fy = (clamp(l, 0, 100) + 16) / 116;
  const fx = a / 500 + fy;
  const fz = fy - b / 200;

  const x50 = XN * labFInv(fx);
  const y50 = YN * labFInv(fy);
  const z50 = ZN * labFInv(fz);

  // Bradford-adapt D50 → D65 to match the sRGB matrix's native white point.
  const [x, y, z] = applyMatrix3(BRADFORD_D50_TO_D65, x50, y50, z50);

  const rl = 3.2404542 * x - 1.5371385 * y - 0.4985314 * z;
  const gl = -0.9692660 * x + 1.8760108 * y + 0.0415560 * z;
  const bl = 0.0556434 * x - 0.2040259 * y + 1.0572252 * z;

  return {
    r: Math.round(clamp(delinearize(rl) * 255, 0, 255)),
    g: Math.round(clamp(delinearize(gl) * 255, 0, 255)),
    b: Math.round(clamp(delinearize(bl) * 255, 0, 255)),
  };
}

// ---------- HSL conversion ----------

export interface HSL { h: number; s: number; l: number; }

export function rgbToHsl(r: number, g: number, b: number): HSL {
  const rn = clamp(r, 0, 255) / 255;
  const gn = clamp(g, 0, 255) / 255;
  const bn = clamp(b, 0, 255) / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === rn) h = ((gn - bn) / d) % 6;
    else if (max === gn) h = (bn - rn) / d + 2;
    else h = (rn - gn) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  const l = (max + min) / 2;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  return { h, s, l };
}

export function hslToRgb(h: number, s: number, l: number): RGB {
  const hh = ((h % 360) + 360) % 360;
  const ss = clamp(s, 0, 1);
  const ll = clamp(l, 0, 1);
  const c = (1 - Math.abs(2 * ll - 1)) * ss;
  const hp = hh / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let r1 = 0, g1 = 0, b1 = 0;
  if (hp >= 0 && hp < 1) { r1 = c; g1 = x; b1 = 0; }
  else if (hp < 2) { r1 = x; g1 = c; b1 = 0; }
  else if (hp < 3) { r1 = 0; g1 = c; b1 = x; }
  else if (hp < 4) { r1 = 0; g1 = x; b1 = c; }
  else if (hp < 5) { r1 = x; g1 = 0; b1 = c; }
  else { r1 = c; g1 = 0; b1 = x; }
  const m = ll - c / 2;
  return {
    r: Math.round((r1 + m) * 255),
    g: Math.round((g1 + m) * 255),
    b: Math.round((b1 + m) * 255),
  };
}

// ---------- Gamut / web-safe warning helpers (#i222) ----------

// Heuristic "out of CMYK print gamut" check. rgbToCmyk()/cmykToRgb() above
// are an exact, lossless CMY formula (no ICC profile), so a literal
// convert-and-back round trip can never detect clipping. Total ink coverage
// (C+M+Y+K) is used as a practical proxy instead: very dark, highly
// saturated colors need disproportionate ink and are exactly the ones that
// look muddy/clipped on real CMYK output, while ordinary saturated colors
// (e.g. pure red/green/blue, ~200% coverage) stay under the limit.
const PRINT_INK_LIMIT_PERCENT = 280;

export function isOutOfPrintGamut(rgb: RGB): boolean {
  const cmyk = rgbToCmyk(rgb.r, rgb.g, rgb.b);
  const totalInkPercent = (cmyk.c + cmyk.m + cmyk.y + cmyk.k) * 100;
  return totalInkPercent > PRINT_INK_LIMIT_PERCENT;
}

/** Desaturate just enough to bring the color back under the print ink limit. */
export function snapToPrintGamut(rgb: RGB): RGB {
  if (!isOutOfPrintGamut(rgb)) return rgb;
  const hsv = rgbToHsv(rgb.r, rgb.g, rgb.b);
  let s = hsv.s;
  for (let i = 0; i < 40 && s > 0; i++) {
    s = Math.max(0, s - 0.025);
    const candidate = hsvToRgb(hsv.h, s, hsv.v);
    if (!isOutOfPrintGamut(candidate)) return candidate;
  }
  return hsvToRgb(hsv.h, 0, hsv.v);
}

// ---------- DOM helpers ----------

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  return node;
}

// ---------- Component ----------

const SV_SIZE = 240;
const HUE_W = 20;
const HUE_H = 240;

export interface ColorPickerCallbacks {
  /** Called with the currently-previewed color when "Add to Swatches" is clicked. */
  onAddToSwatches?: (rgb: RGB) => void;
  /**
   * Called when the eyedropper button is clicked but the browser doesn't
   * support the native EyeDropper API. Should resolve with a sampled RGB
   * (e.g. from the app's canvas), or null if the pick was cancelled.
   */
  onEyedropperFallback?: () => Promise<RGB | null>;
}

export class ColorPicker {
  private root: HTMLDivElement;
  private sheet: HTMLDivElement;
  private svCanvas: HTMLCanvasElement;
  private svCursor: HTMLDivElement;
  private hueCanvas: HTMLCanvasElement;
  private hueCursor: HTMLDivElement;

  private inH: HTMLInputElement;
  private inS: HTMLInputElement;
  private inV: HTMLInputElement;
  private inR: HTMLInputElement;
  private inG: HTMLInputElement;
  private inB: HTMLInputElement;
  private inHex: HTMLInputElement;
  private inC: HTMLInputElement;
  private inM: HTMLInputElement;
  private inY: HTMLInputElement;
  private inK: HTMLInputElement;
  private inL: HTMLInputElement;
  private ina: HTMLInputElement;
  private inb: HTMLInputElement;
  private webSafeToggle: HTMLInputElement;

  private tabRgb: HTMLButtonElement;
  private tabCmyk: HTMLButtonElement;
  private tabLab: HTMLButtonElement;
  private mode: "rgb" | "cmyk" | "lab" = "rgb";

  private currentSwatch: HTMLDivElement;
  private newSwatch: HTMLDivElement;

  private titleEl: HTMLDivElement;
  private gamutWarning: HTMLButtonElement;
  private webSafeWarning: HTMLButtonElement;
  private btnEyedropper: HTMLButtonElement;

  private btnOk: HTMLButtonElement;
  private btnCancel: HTMLButtonElement;
  private btnAddSwatch: HTMLButtonElement;

  // State (HSV is source of truth)
  private hsv: HSV = { h: 0, s: 0, v: 0 };
  private original: RGB = { r: 0, g: 0, b: 0 };
  private webSafe = false;

  private callbacks: ColorPickerCallbacks = {};

  private resolveFn: ((value: RGB | null) => void) | null = null;
  private keyHandler: ((e: KeyboardEvent) => void) | null = null;
  private outsideHandler: ((e: MouseEvent) => void) | null = null;
  private openerEl: Element | null = null;

  constructor() {
    this.root = el("div", "color-picker");
    this.root.style.display = "none";

    // ARIA: modal dialog accessibility (#47)
    this.root.setAttribute("role", "dialog");
    this.root.setAttribute("aria-modal", "true");
    this.root.setAttribute("aria-labelledby", "color-picker-title");

    // Inject tab styles (dark-theme — no light-theme remnants)
    const style = document.createElement("style");
    style.textContent = `
      .color-picker__title-bar {
        display: flex; align-items: center; justify-content: center;
        background: #3c3c3c; height: 22px; padding: 0 8px;
        font-size: 12px; font-weight: 400; color: #d0d0d0;
        border-bottom: 1px solid #1a1a1a; border-radius: 3px 3px 0 0;
        user-select: none;
      }
      .color-picker__tabs { display: flex; gap: 0; margin-bottom: 6px; }
      .color-picker__tab {
        flex: 1; padding: 3px 0; font-size: 11px; font-weight: 400;
        border: none; border-bottom: 2px solid transparent;
        background: #2b2b2b; color: #ccc; cursor: pointer;
        text-align: center; border-radius: 0;
      }
      .color-picker__tab:hover { background: #333; }
      .color-picker__tab--active {
        background: #3a3a3a; color: #e8e8e8;
        border-bottom: 2px solid #d0d0d0;
      }
      .color-picker__field-suffix { font-size: 12px; color: #888; margin-left: 2px; }
    `;
    this.root.appendChild(style);

    // Visible title bar ("Color Picker (Foreground/Background Color)" — not
    // sr-only, per PS design spec. Defaults to "Foreground Color" until
    // open() is called with an explicit target (#i327).
    this.titleEl = el("div", "color-picker__title-bar");
    this.titleEl.id = "color-picker-title";
    this.titleEl.textContent = "Color Picker (Foreground Color)";

    this.sheet = el("div", "color-picker__sheet");
    // The title bar is the dialog's HEADER and must stack ABOVE the sheet.
    // Previously both were appended directly to `.color-picker` — a full-viewport
    // flex-ROW overlay that centers its children — so the title rendered as a
    // detached column to the LEFT of the sheet. Wrap them in a flex-COLUMN
    // window so the overlay centers one dialog with its title on top.
    const win = el("div", "color-picker__window");
    win.appendChild(this.titleEl);
    win.appendChild(this.sheet);
    this.root.appendChild(win);

    // ----- left column: SV square + hue strip -----
    const pickArea = el("div", "color-picker__pick");

    const svWrap = el("div", "color-picker__sv");
    this.svCanvas = el("canvas");
    this.svCanvas.width = SV_SIZE;
    this.svCanvas.height = SV_SIZE;
    this.svCursor = el("div", "color-picker__sv-cursor");
    svWrap.appendChild(this.svCanvas);
    svWrap.appendChild(this.svCursor);

    const hueWrap = el("div", "color-picker__hue");
    this.hueCanvas = el("canvas");
    this.hueCanvas.width = HUE_W;
    this.hueCanvas.height = HUE_H;
    this.hueCursor = el("div", "color-picker__hue-cursor");
    hueWrap.appendChild(this.hueCanvas);
    hueWrap.appendChild(this.hueCursor);

    pickArea.appendChild(svWrap);
    pickArea.appendChild(hueWrap);

    // ----- right column: swatches + fields + actions -----
    const sideCol = el("div", "color-picker__side");

    const swatches = el("div", "color-picker__swatches");
    const newCol = el("div", "color-picker__swatch-col");
    const newLabel = el("div", "color-picker__swatch-label");
    newLabel.textContent = "new";
    this.newSwatch = el("div", "color-picker__swatch-chip");
    newCol.appendChild(newLabel);
    newCol.appendChild(this.newSwatch);

    const curCol = el("div", "color-picker__swatch-col");
    const curLabel = el("div", "color-picker__swatch-label");
    curLabel.textContent = "current";
    this.currentSwatch = el("div", "color-picker__swatch-chip");
    curCol.appendChild(curLabel);
    curCol.appendChild(this.currentSwatch);

    swatches.appendChild(newCol);
    swatches.appendChild(curCol);
    sideCol.appendChild(swatches);

    // Gamut / web-safe warning icons + eyedropper (#i222). Hidden by default
    // via style.display = "none" and toggled on in refreshAll()/updateFields().
    const warnRow = el("div", "color-picker__warnings");
    this.gamutWarning = el("button", "color-picker__icon-btn color-picker__gamut-warning");
    this.gamutWarning.type = "button";
    this.gamutWarning.textContent = "⚠"; // warning triangle
    this.gamutWarning.title = "Not print-safe — click to snap to the nearest in-gamut color";
    this.gamutWarning.setAttribute("aria-label", "Color is outside CMYK print gamut — click to fix");
    this.gamutWarning.style.display = "none";

    this.webSafeWarning = el("button", "color-picker__icon-btn color-picker__websafe-warning");
    this.webSafeWarning.type = "button";
    this.webSafeWarning.textContent = "▦"; // small cube-ish glyph
    this.webSafeWarning.title = "Not a web-safe color — click to snap to the nearest web-safe color";
    this.webSafeWarning.setAttribute("aria-label", "Color is not web-safe — click to fix");
    this.webSafeWarning.style.display = "none";

    this.btnEyedropper = el("button", "color-picker__icon-btn color-picker__eyedropper");
    this.btnEyedropper.type = "button";
    this.btnEyedropper.textContent = "\u{1F4A7}"; // droplet
    this.btnEyedropper.title = "Pick a color from the screen";
    this.btnEyedropper.setAttribute("aria-label", "Eyedropper — pick a color from the screen");

    warnRow.appendChild(this.gamutWarning);
    warnRow.appendChild(this.webSafeWarning);
    warnRow.appendChild(this.btnEyedropper);
    sideCol.appendChild(warnRow);

    // Fields
    const fields = el("div", "color-picker__fields");

    // Mode tabs
    const tabBar = el("div", "color-picker__tabs");
    this.tabRgb = el("button", "color-picker__tab color-picker__tab--active");
    this.tabRgb.type = "button";
    this.tabRgb.textContent = "RGB";
    this.tabCmyk = el("button", "color-picker__tab");
    this.tabCmyk.type = "button";
    this.tabCmyk.textContent = "CMYK";
    this.tabLab = el("button", "color-picker__tab");
    this.tabLab.type = "button";
    this.tabLab.textContent = "Lab";
    tabBar.appendChild(this.tabRgb);
    tabBar.appendChild(this.tabCmyk);
    tabBar.appendChild(this.tabLab);
    fields.appendChild(tabBar);

    const mkField = (label: string, suffix?: string): HTMLInputElement => {
      const row = el("div", "color-picker__field");
      const lab = el("label", "color-picker__field-label");
      lab.textContent = label;
      const input = el("input", "color-picker__field-input");
      input.type = "text";
      input.spellcheck = false;
      row.appendChild(lab);
      row.appendChild(input);
      if (suffix) {
        const suf = el("span", "color-picker__field-suffix");
        suf.textContent = suffix;
        row.appendChild(suf);
      }
      fields.appendChild(row);
      return input;
    };

    // RGB fields
    this.inR = mkField("R:");
    this.inG = mkField("G:");
    this.inB = mkField("B:");

    // CMYK fields (hidden by default)
    this.inC = mkField("C:", "%");
    this.inM = mkField("M:", "%");
    this.inY = mkField("Y:", "%");
    this.inK = mkField("K:", "%");

    // Lab fields (hidden by default)
    this.inL = mkField("L:");
    this.ina = mkField("a:");
    this.inb = mkField("b:");

    // HSV fields (always visible below mode-specific fields)
    const gap = el("div", "color-picker__field-gap");
    fields.appendChild(gap);
    this.inH = mkField("H:", "°");
    this.inS = mkField("S:", "%");
    this.inV = mkField("B:", "%");

    // Hex (slightly wider, with '#' prefix)
    const hexRow = el("div", "color-picker__field");
    const hexLab = el("label", "color-picker__field-label");
    hexLab.textContent = "#";
    this.inHex = el("input", "color-picker__field-input color-picker__field-input--hex");
    this.inHex.type = "text";
    this.inHex.spellcheck = false;
    this.inHex.maxLength = 7;
    const hexPrefix = el("span", "color-picker__field-suffix");
    hexPrefix.textContent = "#";
    hexRow.appendChild(hexLab);
    hexRow.appendChild(hexPrefix);
    hexRow.appendChild(this.inHex);
    fields.appendChild(hexRow);

    sideCol.appendChild(fields);

    // Web colors toggle
    const webRow = el("label", "color-picker__web");
    this.webSafeToggle = el("input");
    this.webSafeToggle.type = "checkbox";
    const webText = el("span");
    webText.textContent = "Only Web Colors";
    webRow.appendChild(this.webSafeToggle);
    webRow.appendChild(webText);
    sideCol.appendChild(webRow);

    // Actions
    const actions = el("div", "color-picker__actions");
    this.btnCancel = el("button", "color-picker__btn");
    this.btnCancel.type = "button";
    this.btnCancel.textContent = "Cancel";
    this.btnOk = el("button", "color-picker__btn color-picker__btn--primary");
    this.btnOk.type = "button";
    this.btnOk.textContent = "OK";
    this.btnAddSwatch = el("button", "color-picker__btn");
    this.btnAddSwatch.type = "button";
    this.btnAddSwatch.textContent = "Add to Swatches";
    actions.appendChild(this.btnCancel);
    actions.appendChild(this.btnOk);
    actions.appendChild(this.btnAddSwatch);
    sideCol.appendChild(actions);

    this.sheet.appendChild(pickArea);
    this.sheet.appendChild(sideCol);

    document.body.appendChild(this.root);

    this.drawHueStrip();
    this.wireEvents();
  }

  // ---------- Public API ----------

  /** Register optional integration callbacks (Add to Swatches, eyedropper fallback). */
  setCallbacks(callbacks: ColorPickerCallbacks): void {
    this.callbacks = callbacks;
  }

  open(initial: RGB, which: "foreground" | "background" = "foreground"): Promise<RGB | null> {
    // Reject in-flight if open() is called twice; we just cancel the previous.
    if (this.resolveFn) {
      const prev = this.resolveFn;
      this.resolveFn = null;
      prev(null);
    }

    // Remember which element opened the dialog so we can restore focus on close.
    this.openerEl = document.activeElement;

    // Distinguish Foreground vs Background in the title bar (#i327), matching
    // Photoshop's "Color Picker (Foreground Color)" / "(Background Color)".
    this.titleEl.textContent =
      which === "background" ? "Color Picker (Background Color)" : "Color Picker (Foreground Color)";

    this.original = {
      r: clamp(Math.round(initial.r), 0, 255),
      g: clamp(Math.round(initial.g), 0, 255),
      b: clamp(Math.round(initial.b), 0, 255),
    };
    this.hsv = rgbToHsv(this.original.r, this.original.g, this.original.b);
    this.webSafe = false;
    this.webSafeToggle.checked = false;

    this.paintCurrentSwatch();
    this.refreshAll();

    this.root.style.display = "";

    // Focus the hex field for quick keyboard entry.
    setTimeout(() => this.inHex.focus(), 0);

    return new Promise<RGB | null>((resolve) => {
      this.resolveFn = resolve;
    });
  }

  close(): void {
    this.finish(null);
  }

  destroy(): void {
    if (this.keyHandler) window.removeEventListener("keydown", this.keyHandler, true);
    this.keyHandler = null;
    if (this.outsideHandler) this.root.removeEventListener("mousedown", this.outsideHandler);
    this.outsideHandler = null;
    if (this.root.parentNode) this.root.parentNode.removeChild(this.root);
  }

  // ---------- Internal: finish / resolve ----------

  private finish(result: RGB | null): void {
    if (!this.resolveFn) return;
    const fn = this.resolveFn;
    this.resolveFn = null;
    this.root.style.display = "none";
    // Restore focus to the element that opened the dialog (#47).
    if (this.openerEl && "focus" in this.openerEl) {
      (this.openerEl as HTMLElement).focus();
    }
    this.openerEl = null;
    fn(result);
  }

  private currentRgb(): RGB {
    const rgb = hsvToRgb(this.hsv.h, this.hsv.s, this.hsv.v);
    return this.webSafe ? snapWebSafe(rgb) : rgb;
  }

  // ---------- Wire events ----------

  private wireEvents(): void {
    // Outside click cancels.
    this.outsideHandler = (e: MouseEvent) => {
      if (e.target === this.root) this.finish(null);
    };
    this.root.addEventListener("mousedown", this.outsideHandler);

    // Stop clicks inside sheet from bubbling to outside handler (already handled by target check, but safety).
    this.sheet.addEventListener("mousedown", (e) => e.stopPropagation());

    // Keyboard.
    this.keyHandler = (e: KeyboardEvent) => {
      if (this.root.style.display === "none") return;
      if (e.key === "Escape") {
        e.preventDefault();
        this.finish(null);
      } else if (e.key === "Enter") {
        // Don't fire OK if user is in mid-edit on a field and needs to commit a value first;
        // simplest correct behavior: commit the focused input, then OK.
        const tgt = document.activeElement;
        if (tgt instanceof HTMLInputElement) {
          tgt.dispatchEvent(new Event("change"));
        }
        e.preventDefault();
        this.finish(this.currentRgb());
      } else if (e.key === "Tab") {
        // Focus-trap: keep Tab / Shift+Tab within the dialog (#47).
        trapFocus(e, this.sheet);
      }
    };
    window.addEventListener("keydown", this.keyHandler, true);

    // Buttons
    this.btnOk.addEventListener("click", () => this.finish(this.currentRgb()));
    this.btnCancel.addEventListener("click", () => this.finish(null));

    // Tab switching
    this.tabRgb.addEventListener("click", () => this.setMode("rgb"));
    this.tabCmyk.addEventListener("click", () => this.setMode("cmyk"));
    this.tabLab.addEventListener("click", () => this.setMode("lab"));

    // SV canvas drag
    this.attachDrag(this.svCanvas, (x, y, rect) => {
      const nx = clamp(x / rect.width, 0, 1);
      const ny = clamp(y / rect.height, 0, 1);
      this.hsv = { h: this.hsv.h, s: nx, v: 1 - ny };
      this.refreshAll();
    });

    // Hue canvas drag
    this.attachDrag(this.hueCanvas, (_x, y, rect) => {
      const ny = clamp(y / rect.height, 0, 1);
      this.hsv = { h: ny * 360, s: this.hsv.s, v: this.hsv.v };
      this.refreshAll();
    });

    // Field bindings
    const commitHSV = () => {
      const h = parseFloat(this.inH.value);
      const s = parseFloat(this.inS.value);
      const v = parseFloat(this.inV.value);
      if (Number.isFinite(h) && Number.isFinite(s) && Number.isFinite(v)) {
        this.hsv = {
          h: ((h % 360) + 360) % 360,
          s: clamp(s, 0, 100) / 100,
          v: clamp(v, 0, 100) / 100,
        };
        this.refreshAll();
      } else {
        this.refreshAll();
      }
    };
    for (const inp of [this.inH, this.inS, this.inV]) {
      inp.addEventListener("change", commitHSV);
    }

    const commitRGB = () => {
      const r = parseInt(this.inR.value, 10);
      const g = parseInt(this.inG.value, 10);
      const b = parseInt(this.inB.value, 10);
      if (Number.isFinite(r) && Number.isFinite(g) && Number.isFinite(b)) {
        const rgb: RGB = {
          r: clamp(r, 0, 255),
          g: clamp(g, 0, 255),
          b: clamp(b, 0, 255),
        };
        this.setFromRgb(rgb);
      } else {
        this.refreshAll();
      }
    };
    for (const inp of [this.inR, this.inG, this.inB]) {
      inp.addEventListener("change", commitRGB);
    }

    const commitHex = () => {
      const parsed = hexToRgb(this.inHex.value);
      if (parsed) {
        this.setFromRgb(parsed);
      } else {
        // revert
        this.refreshAll();
      }
    };
    this.inHex.addEventListener("change", commitHex);

    const commitCMYK = () => {
      const c = parseFloat(this.inC.value);
      const m = parseFloat(this.inM.value);
      const y = parseFloat(this.inY.value);
      const k = parseFloat(this.inK.value);
      if (Number.isFinite(c) && Number.isFinite(m) && Number.isFinite(y) && Number.isFinite(k)) {
        const rgb = cmykToRgb(
          clamp(c, 0, 100) / 100,
          clamp(m, 0, 100) / 100,
          clamp(y, 0, 100) / 100,
          clamp(k, 0, 100) / 100,
        );
        this.setFromRgb(rgb);
      } else {
        this.refreshAll();
      }
    };
    for (const inp of [this.inC, this.inM, this.inY, this.inK]) {
      inp.addEventListener("change", commitCMYK);
    }

    const commitLab = () => {
      const l = parseFloat(this.inL.value);
      const a = parseFloat(this.ina.value);
      const b = parseFloat(this.inb.value);
      if (Number.isFinite(l) && Number.isFinite(a) && Number.isFinite(b)) {
        const rgb = labToRgb(clamp(l, 0, 100), clamp(a, -128, 127), clamp(b, -128, 127));
        this.setFromRgb(rgb);
      } else {
        this.refreshAll();
      }
    };
    for (const inp of [this.inL, this.ina, this.inb]) {
      inp.addEventListener("change", commitLab);
    }

    this.webSafeToggle.addEventListener("change", () => {
      this.webSafe = this.webSafeToggle.checked;
      // When enabling, snap current value so it reflects immediately.
      if (this.webSafe) {
        const snapped = snapWebSafe(hsvToRgb(this.hsv.h, this.hsv.s, this.hsv.v));
        this.setFromRgb(snapped);
      } else {
        this.refreshAll();
      }
    });

    // Click the "current" swatch to revert the working color back to what
    // was passed into open() (#i220).
    this.currentSwatch.addEventListener("click", () => this.setFromRgb(this.original));

    // "Add to Swatches" hands the previewed color off to the host app (#i215).
    this.btnAddSwatch.addEventListener("click", () => {
      this.callbacks.onAddToSwatches?.(this.currentRgb());
    });

    // Gamut warning: click to snap to the nearest color this app considers
    // safe for CMYK print output (#i222).
    this.gamutWarning.addEventListener("click", () => {
      this.setFromRgb(snapToPrintGamut(this.currentRgb()));
    });

    // Web-safe warning: click to snap to the nearest web-safe color (#i222).
    this.webSafeWarning.addEventListener("click", () => {
      this.setFromRgb(snapWebSafe(this.currentRgb()));
    });

    // Eyedropper: use the native browser API where supported; otherwise fall
    // back to a host-supplied sampler (e.g. sampling the app's canvas) (#i222).
    this.btnEyedropper.addEventListener("click", async () => {
      const NativeEyeDropper = (window as any).EyeDropper;
      if (typeof NativeEyeDropper === "function") {
        try {
          const instance = new NativeEyeDropper();
          const result = await instance.open();
          const rgb = hexToRgb(result?.sRGBHex ?? "");
          if (rgb) this.setFromRgb(rgb);
        } catch {
          // User cancelled the native picker (Escape) — no-op.
        }
        return;
      }
      if (this.callbacks.onEyedropperFallback) {
        const rgb = await this.callbacks.onEyedropperFallback();
        if (rgb) this.setFromRgb(rgb);
      }
    });
  }

  private setFromRgb(rgb: RGB): void {
    const newHsv = rgbToHsv(rgb.r, rgb.g, rgb.b);
    // Preserve hue when the converted RGB has zero chroma (pure gray / black / white)
    // — otherwise the hue ring would snap to 0 every time the user goes to grayscale.
    if (rgb.r === rgb.g && rgb.g === rgb.b) {
      this.hsv = { h: this.hsv.h, s: 0, v: newHsv.v };
    } else {
      this.hsv = newHsv;
    }
    this.refreshAll();
  }

  private setMode(mode: "rgb" | "cmyk" | "lab"): void {
    this.mode = mode;
    this.tabRgb.classList.toggle("color-picker__tab--active", mode === "rgb");
    this.tabCmyk.classList.toggle("color-picker__tab--active", mode === "cmyk");
    this.tabLab.classList.toggle("color-picker__tab--active", mode === "lab");
    this.refreshAll();
  }

  // ---------- Drag helper ----------

  private attachDrag(
    target: HTMLElement,
    onMove: (x: number, y: number, rect: DOMRect) => void,
  ): void {
    const handle = (e: PointerEvent) => {
      const rect = target.getBoundingClientRect();
      onMove(e.clientX - rect.left, e.clientY - rect.top, rect);
    };
    target.addEventListener("pointerdown", (e) => {
      e.preventDefault();
      (e.target as Element).setPointerCapture?.(e.pointerId);
      handle(e);
      const move = (ev: PointerEvent) => handle(ev);
      const up = (ev: PointerEvent) => {
        target.removeEventListener("pointermove", move);
        target.removeEventListener("pointerup", up);
        target.removeEventListener("pointercancel", up);
        (ev.target as Element).releasePointerCapture?.(ev.pointerId);
      };
      target.addEventListener("pointermove", move);
      target.addEventListener("pointerup", up);
      target.addEventListener("pointercancel", up);
    });
  }

  // ---------- Painting ----------

  private drawHueStrip(): void {
    const ctx = this.hueCanvas.getContext("2d");
    if (!ctx) return;
    const grad = ctx.createLinearGradient(0, 0, 0, HUE_H);
    // 7 stops covering full hue circle (top = 0/360 red, bottom = 360 red).
    const stops: Array<[number, string]> = [
      [0,    "#ff0000"],
      [1/6,  "#ffff00"],
      [2/6,  "#00ff00"],
      [3/6,  "#00ffff"],
      [4/6,  "#0000ff"],
      [5/6,  "#ff00ff"],
      [1,    "#ff0000"],
    ];
    for (const [pos, col] of stops) grad.addColorStop(pos, col);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, HUE_W, HUE_H);
  }

  private drawSvSquare(): void {
    const ctx = this.svCanvas.getContext("2d");
    if (!ctx) return;
    const w = SV_SIZE;
    const h = SV_SIZE;

    // Fill with the pure hue.
    const pure = hsvToRgb(this.hsv.h, 1, 1);
    ctx.fillStyle = `rgb(${pure.r},${pure.g},${pure.b})`;
    ctx.fillRect(0, 0, w, h);

    // Horizontal white gradient (saturation: 0 -> hue).
    const wg = ctx.createLinearGradient(0, 0, w, 0);
    wg.addColorStop(0, "rgba(255,255,255,1)");
    wg.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = wg;
    ctx.fillRect(0, 0, w, h);

    // Vertical black gradient (value: 1 at top -> 0 at bottom).
    const bg = ctx.createLinearGradient(0, 0, 0, h);
    bg.addColorStop(0, "rgba(0,0,0,0)");
    bg.addColorStop(1, "rgba(0,0,0,1)");
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, w, h);
  }

  // ---------- Refresh (re-render from state) ----------

  private refreshAll(): void {
    this.drawSvSquare();
    this.positionCursors();
    this.updateFields();
    this.paintNewSwatch();
  }

  private positionCursors(): void {
    const sx = this.hsv.s * SV_SIZE;
    const sy = (1 - this.hsv.v) * SV_SIZE;
    this.svCursor.style.left = `${sx}px`;
    this.svCursor.style.top = `${sy}px`;

    const hy = (this.hsv.h / 360) * HUE_H;
    this.hueCursor.style.top = `${hy}px`;
  }

  private updateFields(): void {
    const rgb = this.currentRgb();
    // Mode-specific field visibility
    const showRgb = this.mode === "rgb";
    const showCmyk = this.mode === "cmyk";
    const showLab = this.mode === "lab";
    for (const inp of [this.inR, this.inG, this.inB]) {
      (inp.closest(".color-picker__field") as HTMLElement).style.display = showRgb ? "" : "none";
    }
    for (const inp of [this.inC, this.inM, this.inY, this.inK]) {
      (inp.closest(".color-picker__field") as HTMLElement).style.display = showCmyk ? "" : "none";
    }
    for (const inp of [this.inL, this.ina, this.inb]) {
      (inp.closest(".color-picker__field") as HTMLElement).style.display = showLab ? "" : "none";
    }

    // RGB values
    this.inR.value = String(rgb.r);
    this.inG.value = String(rgb.g);
    this.inB.value = String(rgb.b);

    // CMYK values
    const cmyk = rgbToCmyk(rgb.r, rgb.g, rgb.b);
    this.inC.value = String(Math.round(cmyk.c * 100));
    this.inM.value = String(Math.round(cmyk.m * 100));
    this.inY.value = String(Math.round(cmyk.y * 100));
    this.inK.value = String(Math.round(cmyk.k * 100));

    // Lab values
    const lab = rgbToLab(rgb.r, rgb.g, rgb.b);
    this.inL.value = String(Math.round(lab.l));
    this.ina.value = String(Math.round(lab.a));
    this.inb.value = String(Math.round(lab.b));

    // HSV (always visible)
    this.inH.value = String(Math.round(this.hsv.h));
    this.inS.value = String(Math.round(this.hsv.s * 100));
    this.inV.value = String(Math.round(this.hsv.v * 100));

    // Hex with '#' prefix
    this.inHex.value = "#" + rgbToHex(rgb.r, rgb.g, rgb.b);

    // Gamut / web-safe warning icon visibility (#i222).
    this.gamutWarning.style.display = isOutOfPrintGamut(rgb) ? "" : "none";
    const snapped = snapWebSafe(rgb);
    const isWebSafe = snapped.r === rgb.r && snapped.g === rgb.g && snapped.b === rgb.b;
    this.webSafeWarning.style.display = isWebSafe ? "none" : "";
  }

  private paintCurrentSwatch(): void {
    const { r, g, b } = this.original;
    this.currentSwatch.style.background = `rgb(${r},${g},${b})`;
  }

  private paintNewSwatch(): void {
    const { r, g, b } = this.currentRgb();
    this.newSwatch.style.background = `rgb(${r},${g},${b})`;
  }
}

// ── Shared a11y helpers ────────────────────────────────────────────────────

const FOCUSABLE_SELECTORS = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

/**
 * Trap Tab / Shift+Tab focus within `container`. Call from a keydown handler.
 * Closes the standard "roving focus" a11y pattern for modal dialogs (#47).
 */
export function trapFocus(e: KeyboardEvent, container: HTMLElement): void {
  if (e.key !== "Tab") return;
  const focusable = Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS),
  ).filter((el) => !el.closest("[style*='display: none']") && !el.closest("[style*='display:none']"));
  if (focusable.length === 0) return;
  const first = focusable[0]!;
  const last = focusable[focusable.length - 1]!;
  if (e.shiftKey) {
    if (document.activeElement === first) {
      e.preventDefault();
      last.focus();
    }
  } else {
    if (document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
}
